import React, { createContext, useContext, useEffect, useState } from "react";

export interface ExportJob {
  id: string;
  name: string;
  folderId?: string;
}

interface ExportContextType {
  jobs: ExportJob[];
  addJob: (job: ExportJob) => void;
  updateJob: (id: string, updates: Partial<ExportJob>) => void;
  removeJob: (id: string) => void;
  clearAllJobs: () => void;
}

const ExportContext = createContext<ExportContextType | null>(null);
const STORAGE_KEY = "purl_app_exports";

export const ExportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [jobs, setJobs] = useState<ExportJob[]>([]);

  // Load from localStorage on app load
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setJobs(JSON.parse(stored));
      } catch (e) {
        console.error("Failed to parse stored exports", e);
      }
    }
  }, []);

  // Persist on change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  }, [jobs]);

  const addJob = (job: ExportJob) => {
    setJobs((prev) => {
      if (prev.find((j) => j.id === job.id)) return prev;
      return [...prev, job];
    });
  };

  const updateJob = (id: string, updates: Partial<ExportJob>) => {
    setJobs((prev) =>
      prev.map((job) => (job.id === id ? { ...job, ...updates } : job))
    );
  };

  const removeJob = (id: string) => {
    setJobs((prev) => prev.filter((job) => job.id !== id));
  };

  const clearAllJobs = () => {
    setJobs([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <ExportContext.Provider
      value={{
        jobs,
        addJob,
        updateJob,
        removeJob,
        clearAllJobs,
      }}
    >
      {children}
    </ExportContext.Provider>
  );
};

export const useExport = () => {
  const ctx = useContext(ExportContext);
  if (!ctx) {
    throw new Error("useExport must be used inside ExportProvider");
  }
  return ctx;
};
